"use strict";
/**
 * Settings Modal Handler
 *
 * Manages the settings modal lifecycle:
 * - Open / close / overlay-dismiss
 * - Form submission: reads form values, merges with current settings,
 *   persists to sessionStorage, and synchronises all dependent UI.
 * - Reset-to-defaults button
 * - Sell-mode toggle visibility
 *
 * Note: the sport-mode flags (baseball/basketball/soccer) are NOT managed by this
 * modal — they are driven solely by the sport switcher for the current market
 * (services/market-selection-service.ts). Saving the form must leave them
 * untouched. Manual-price mode and a sport mode can be active simultaneously
 * (combined mode), which enables per-button manual pricing on the sport
 * interface.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupSettingsModal = setupSettingsModal;
const state_1 = require("../../state");
const ui_1 = require("../../ui");
const connection_check_1 = require("../../ui/connection-check");
const baseball_controls_1 = require("../order/baseball-controls");
const order_buttons_1 = require("../order/basketball/order-buttons");
const order_buttons_2 = require("../order/soccer/order-buttons");
/**
 * Wire up all event listeners for the settings modal.
 * Called once from `initApp`.
 */
function setupSettingsModal() {
    // Owner-only network diagnostic. Visibility is governed by the
    // `connection-check` gate in gates.ts, not here — wiring the listener
    // unconditionally is safe because a hidden control cannot be clicked.
    (0, connection_check_1.setupConnectionCheck)();
    const closeBtn = document.getElementById("close-modal");
    const cancelBtn = document.getElementById("cancel-settings-btn");
    const form = document.getElementById("settings-form");
    const overlay = document.querySelector(".modal-overlay");
    // --- Close / cancel / overlay dismiss ---
    if (closeBtn) {
        closeBtn.addEventListener("click", () => (0, ui_1.hideSettingsModal)());
    }
    if (cancelBtn) {
        cancelBtn.addEventListener("click", () => (0, ui_1.hideSettingsModal)());
    }
    if (overlay) {
        overlay.addEventListener("click", (e) => {
            if (e.target === overlay)
                (0, ui_1.hideSettingsModal)();
        });
    }
    // --- Sell-mode toggle: show/hide "Shares to Sell" input ---
    const sellModeToggle = document.getElementById("sell-mode-toggle");
    const fixedSellGroup = document.getElementById("fixed-sell-quantity-group");
    if (sellModeToggle && fixedSellGroup) {
        sellModeToggle.addEventListener("change", () => {
            fixedSellGroup.classList.toggle("hidden", !sellModeToggle.checked);
        });
    }
    // --- Form submission ---
    if (form) {
        form.addEventListener("submit", (e) => {
            e.preventDefault();
            try {
                const currentSettings = (0, state_1.getState)().tradingSettings;
                const formSettings = (0, ui_1.getSettingsFormValues)();
                // Preserve independent-slippage when manual-price or a sport mode is active
                const preserveIndependentSlippage = currentSettings.manualPriceMode === true ||
                    currentSettings.baseballMode === true ||
                    currentSettings.basketballMode === true ||
                    currentSettings.soccerMode === true ||
                    formSettings.manualPriceMode === true;
                const newSettings = {
                    ...currentSettings,
                    ...formSettings,
                    independentSlippage: preserveIndependentSlippage
                        ? currentSettings.independentSlippage
                        : formSettings.independentSlippage,
                };
                (0, state_1.updateState)({ tradingSettings: newSettings });
                (0, state_1.saveSettingsToStorage)();
                // Sync manual price controls visibility.
                // In combined mode (a sport layout + manual price both active), the
                // global manual-price slider is hidden in favour of per-button
                // steppers. Under forceDefaultButtons no sport layout shows, so
                // the global slider comes back.
                const isCombined = (0, ui_1.isCombinedPricingMode)(newSettings);
                (0, ui_1.updateManualPriceVisibility)(newSettings.manualPriceMode === true && !isCombined);
                const state = (0, state_1.getState)();
                (0, ui_1.updateManualPriceSlider)("yes", state.tradingSettings.manualYesPrice, state.tradingSettings.manualYesPriceSelected);
                (0, ui_1.updateManualPriceSlider)("no", state.tradingSettings.manualNoPrice, state.tradingSettings.manualNoPriceSelected);
                // Sync sell mode badges on order buttons
                (0, ui_1.updateSellModeIndicator)(state.tradingSettings.sellMode ?? "full", state.tradingSettings.fixedSellQuantity ?? 1);
                // Sync order-button layout visibility (sport modes + force-default)
                (0, ui_1.updateOrderLayoutVisibility)(state.tradingSettings);
                (0, ui_1.updateBaseballFactorDisplay)(state.tradingSettings.baseballFactor ?? 0);
                (0, ui_1.updateBasketballFactorDisplay)(state.tradingSettings.baseballFactor ?? 0);
                (0, ui_1.updateSoccerFactorDisplay)(state.tradingSettings.baseballFactor ?? 0);
                // Sync per-button stepper and Set-to-Ask visibility for combined mode
                (0, ui_1.updateBaseballManualPriceDisplay)(isCombined);
                if (state.tradingSettings.baseballMode === true) {
                    (0, baseball_controls_1.refreshAllHints)();
                }
                if (state.tradingSettings.basketballMode === true) {
                    (0, order_buttons_1.refreshBasketballHints)();
                }
                if (state.tradingSettings.soccerMode === true) {
                    (0, order_buttons_2.refreshSoccerHints)();
                }
                // Sync slippage bar visibility (hidden when manual price or a
                // sport layout is active)
                (0, ui_1.updateSlippageVisibility)(state.tradingSettings);
                (0, ui_1.hideSettingsModal)();
                (0, ui_1.showError)("Settings saved", "market-error");
                setTimeout(() => (0, ui_1.hideError)("market-error"), 2000);
            }
            catch (error) {
                console.error("Error saving settings:", error);
                (0, ui_1.showOrderError)("Failed to save settings");
            }
        });
    }
}
